import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { openCenteredModal } from "../../REDUX/actions/centeredModal";
import TransporterSelectModal from "./DepartureTable/TransporterSelectModal";
import VerticalPoints from "../Common/VerticalPoints/VerticalPoints";
import { openRightModal } from "../../REDUX/actions/rightModal";
import UpdateDeparture from "./UpdateDeparture";
import { getProductType } from "../../Utils";
import API from "../../Api/Api";
import {
  DownloadAddress,
  DownloadPackingList,
} from "../../Utils/deliveryUtils";

const statusLabel = {
  TRANSP: "TRANSP",
  TO_PREP: "TO PREP",
  INCIDENT: "INCIDENT",
  READY: "READY",
  RECUP: "RETRIEVAL",
  DELIVERY: "DELIVERY",
  DELIVERED: "DELIVERED",
  INVOICE: "INVOICED",
};

export const statusColors = {
  TRANSP: "#fbb9b9",
  TO_PREP: "#ff8282",
  INCIDENT: "#ff6666",
  READY: "#d79964",
  RECUP: "#42d08b",
  DELIVERY: "#4dae76",
  DELIVERED: "#808080",
  INVOICE: "#808080",
};

export const statusOptions = (t) => [
  {
    name: t("TRANSPORT"),
    code: "TRANSP",
  },
  {
    name: t("TO PREPARE"),
    code: "TO_PREP",
  },
  {
    name: t("INCIDENT"),
    code: "INCIDENT",
  },
  {
    name: t("READY"),
    code: "READY",
  },
  {
    name: t("RETRIEVAL"),
    code: "RECUP",
  },
  {
    name: t("DELIVERY"),
    code: "DELIVERY",
  },
  {
    name: t("DELIVERED"),
    code: "DELIVERED",
  },
];

export const useStats = () => {
  const { user } = useSelector((state) => state.userReducer);
  const getStats = async (status, signal) => {
    let filters = {};

    if (user?.attributes?.userRole === 0) {
      filters = {
        ...filters,
        commande: {
          reseller: {
            profils: {
              user: {
                id: {
                  $eq: user?.id,
                },
              },
            },
          },
        },
      };
    } else if (user?.attributes?.userRole === 3) {
      filters = {
        ...filters,
        commande: {
          packings: {
            approvisionnement: {
              realEntrepot: {
                storekeepers: {
                  user: {
                    id: {
                      $eq: user?.id,
                    },
                  },
                },
              },
            },
          },
        },
      };
    } else if (user?.attributes?.userRole === 2) {
      filters = {
        ...filters,
        $or: [
          {
            commande: {
              business_affiliate: {
                user: {
                  id: {
                    $eq: user?.id,
                  },
                },
              },
            },
          },
          {
            commande: {
              reseller: {
                business_affiliate: {
                  user: {
                    id: {
                      $eq: user?.id,
                    },
                  },
                },
              },
            },
          },
        ],
      };
    } else if (user?.attributes?.userRole === 4) {
      filters = {
        ...filters,
        transporter: {
          id: {
            $eq: user?.attributes?.transporter?.data?.id,
          },
        },
      };
    }
    try {
      let statusFilters = {
        $or: [
          {
            deliveryStatus: Array.isArray(status)
              ? {
                  $in: status,
                }
              : {
                  $eq: status,
                },
          },
          {
            ...(status === "READY"
              ? {
                  deliveryStatus: {
                    $null: true,
                  },
                }
              : {}),
            status: Array.isArray(status)
              ? {
                  $in: status,
                }
              : {
                  $eq: status,
                },
          },
        ],
      };

      if (user?.attributes?.userRole === 0) {
        if (status === "APPROV") {
          statusFilters = {
            commande: {
              packings: {
                approvisionnement: {
                  arrivalDate: {
                    $gt: moment().utc(true).startOf("day").format("YYYY-MM-DD"),
                  },
                },
              },
            },
          };
        } else if (status === "ORDER") {
          statusFilters = {
            commande: {
              realDeliveryDate: {
                $null: true,
              },
            },
          };
        }
      }

      const { data } = await API.get("api/deliveries", {
        signal,
        params: {
          filters: {
            $and: [filters, statusFilters],
          },
        },
      });

      return data?.data?.length || 0;
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
    }
  };
  return { getStats };
};

export const useDeparture = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const [optionsData, setOptionsData] = useState({});
  const [stats, setStats] = useState([]);
  const { t } = useTranslation("common");
  const { getStats } = useStats();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStats = async () => {
      try {
        let data;
        if (user?.attributes?.userRole === 3) {
          const [incident, toPrepare, ready, pickedUp] = await Promise.all([
            getStats("INCIDENT", signal),
            getStats("TO_PREP", signal),
            getStats("READY", signal),
            getStats("RECUP", signal),
          ]);

          data = [
            { label: t("Incident"), value: incident, color: "#ff3131" },
            { label: t("To prepare"), value: toPrepare, color: "#ff5757" },
            { label: t("Ready to pickup"), value: ready, color: "#c9762e" },
            {
              label: `${t("Picked up in")} ${new Date().getFullYear()}`,
              value: pickedUp,
              color: "#00bf63",
            },
          ];
        } else {
          const [incident, toPrepare, ready, delivery] = await Promise.all([
            getStats("INCIDENT", signal),
            getStats("TO_PREP", signal),
            getStats("READY", signal),
            getStats("DELIVERY", signal),
          ]);

          data = [
            { label: t("Incident"), value: incident, color: "#ff3131" },
            { label: t("To prepare"), value: toPrepare, color: "#ff5757" },
            { label: t("Ready to pickup"), value: ready, color: "#c9762e" },
            {
              label: t("In the process of delivery"),
              value: delivery,
              color: "#00bf63",
            },
          ];
        }

        setStats(data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.error("Failed to fetch stats:", error);
      }
    };

    fetchStats();
    return () => {
      controller.abort();
    };
  }, [user, t]);

  const options = useCallback(
    (row) => {
      if ([1, 2].includes(user?.attributes?.userRole)) {
        return [
          {
            text: t("Transporter selection"),
            callback: () =>
              dispatch(
                openCenteredModal(<TransporterSelectModal delivery={row} />),
              ),
          },
          {
            text: t("Invoice"),
            hidden: user?.attributes?.userRole !== 1,
            callback: () =>
              dispatch(
                openRightModal(
                  <UpdateDeparture id={row.id} initialTab="Invoice" />,
                ),
              ),
          },
          {
            text: t("Reception"),
            callback: () =>
              dispatch(
                openRightModal(
                  <UpdateDeparture id={row.id} initialTab="Reception" />,
                ),
              ),
          },
          {
            text: t("Exchange"),
            callback: () =>
              dispatch(
                openRightModal(
                  <UpdateDeparture id={row.id} initialTab="Exchange" />,
                ),
              ),
          },
          {
            text: t("Download Packing List"),
            callback: () =>
              DownloadPackingList(row?.id, row?.attributes?.number, t),
          },
        ];
      } else if (user?.attributes?.userRole === 3) {
        return [
          {
            text: t("Preparation"),
            callback: () =>
              dispatch(
                openRightModal(
                  <UpdateDeparture id={row.id} initialTab="Preparation" />,
                ),
              ),
          },
          {
            text: t("Download Address (PDF)"),
            callback: () =>
              DownloadAddress(row?.id, row?.attributes?.number, t),
          },
          {
            text: t("Download Packing List (PDF)"),
            callback: () =>
              DownloadPackingList(row?.id, row?.attributes?.number, t),
          },
          {
            text: t("Exchange"),
            callback: () =>
              dispatch(
                openRightModal(
                  <UpdateDeparture id={row.id} initialTab="Exchange" />,
                ),
              ),
          },
        ];
      }

      return [];
    },
    [user, dispatch, t],
  );

  const headers = useMemo(() => {
    if (user?.attributes?.userRole === 3) {
      return [
        "Date",
        "Order",
        "Type",
        "Store",
        "Depot",
        "Recup Date",
        "Preparator",
        "Transporter",
        "N°",
        "Status",
        "",
      ];
    }
    return [
      "Date",
      "Order",
      "Type",
      "Store",
      "Depot",
      "Recup Date",
      "Preparator",
      "Transporter",
      "N°",
      "Delivery Date",
      "Status",
      "",
    ];
  }, [user]);

  const columns = [
    {
      name: "date",
      render: (row) =>
        row?.attributes?.createdAt
          ? moment(row?.attributes?.createdAt).format("DD/MM/YYYY")
          : "-",
    },
    {
      name: "commande",
      render: (row) =>
        row?.attributes?.commande?.data?.attributes?.number ?? "-",
    },
    {
      name: "type",
      render: (row) => {
        const packings =
          row?.attributes?.commande?.data?.attributes?.packings?.data;
        const categoryPacking = packings?.find((packing) => {
          const currentCategory =
            packing?.attributes?.model?.data?.attributes?.category?.data;
          if (currentCategory) {
            return true;
          }
          return false;
        });

        const category =
          categoryPacking?.attributes?.model?.data?.attributes?.category?.data
            ?.attributes?.name;

        return (
          category ??
          getProductType(
            packings?.[0]?.attributes?.model?.data?.attributes?.productType,
            t,
          ) ??
          "-"
        );
      },
    },
    {
      name: "reseller",
      render: (row) =>
        row?.attributes?.commande?.data?.attributes.reseller?.data?.attributes
          ?.name ?? "-",
    },
    {
      name: "depot",
      render: (row) =>
        row?.attributes?.commande?.data?.attributes?.packings?.data?.[0]
          ?.attributes?.approvisionnement?.data?.attributes?.realEntrepot?.data
          ?.attributes?.name ?? "-",
    },
    {
      name: "recupDate",
      render: (row) =>
        row?.attributes?.recupDate
          ? moment(row?.attributes?.recupDate).format("DD/MM/YYYY")
          : "-",
    },
    {
      name: "preparator",
      render: (row) =>
        row?.attributes?.storekeeper?.data?.attributes?.name ?? "-",
    },
    {
      name: "transporter",
      render: (row) =>
        row?.attributes?.transporter?.data?.attributes?.name ?? "-",
    },
    {
      name: "number",
      render: (row) => row?.attributes?.number ?? "-",
    },
    {
      name: "deliveryDate",
      hidden: user?.attributes?.userRole === 3,
      render: (row) =>
        row?.attributes?.date
          ? moment(row?.attributes?.date).format("DD/MM/YYYY")
          : "-",
    },
    {
      name: "status",
      render: (row) => (
        <div
          className="py-1 px-2 br14 text-white sBold"
          style={{
            backgroundColor:
              statusColors[
                [
                  "RECUP",
                  "DELIVERY",
                  "DELIVERED",
                  "INCIDENT",
                  "INVOICE",
                ]?.includes(row?.attributes?.deliveryStatus) &&
                row?.attributes?.status === "READY"
                  ? row?.attributes?.deliveryStatus
                  : row?.attributes?.status
              ],
          }}
        >
          {t(
            `${
              statusLabel[
                [
                  "RECUP",
                  "DELIVERY",
                  "DELIVERED",
                  "INCIDENT",
                  "INVOICE",
                ]?.includes(row?.attributes?.deliveryStatus) &&
                row?.attributes?.status === "READY"
                  ? row?.attributes?.deliveryStatus
                  : row?.attributes?.status
              ]
            }`,
          )}{" "}
        </div>
      ),
    },
    {
      name: "options",
      render: (row) => {
        return (
          <VerticalPoints showLeft className="ms-auto" options={options(row)} />
        );
      },
    },
  ];

  const optionsAbortControllerRef = useRef(null);

  const getOptionsData = useCallback(async (collections = []) => {
    optionsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    optionsAbortControllerRef.current = controller;
    try {
      if (collections?.includes("storekeeper")) {
        const { data } = await API.get("api/storekeepers", {
          signal: controller.signal,
        });

        setOptionsData({
          storekeeper: data?.data?.map((option) => ({
            name: option?.attributes?.name,
            code: option?.id,
          })),
        });
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
    }
  }, []);
  return { stats, headers, columns, getOptionsData, optionsData };
};
